// src/components/Header/navMenuItems.ts
// ★ Data-driven navigation menu — single source of truth for all nav links.
// Header.tsx and MobileOffcanvasNav.tsx both consume this data to render menus.

import { locationSuffix } from '../../../config/routes';
import CLINIC_CONFIG from '../../../config/clinicConfig';

// ─── Types ───────────────────────────────────────────────────────────

export interface MenuItem {
    label: string;
    to: string;
    /** If true, this is an external link (opens in new tab) */
    external?: boolean;
    /** Nested children — rendered as dropdown or submenu */
    children?: MenuItem[];
}

// ─── Menu Data ───────────────────────────────────────────────────────

export const mainMenuItems: MenuItem[] = [
    // 1. About Us
    { label: 'About Us', to: '/about-us' },

    // 2. Services (mega dropdown)
    {
        label: 'Services',
        to: '/services',
        children: [
            {
                label: 'Routine Tooth Checkup',
                to: `/routine-tooth-checkup${locationSuffix}`,
                children: [
                    { label: 'Missing Teeth', to: `/missing-teeth${locationSuffix}` },
                    { label: 'Tooth Infection', to: `/tooth-infection${locationSuffix}` },
                    { label: 'Tooth Decay', to: `/tooth-decay${locationSuffix}` },
                    { label: 'Weak Tooth', to: `/weak-tooth${locationSuffix}` },
                ],
            },
            {
                label: 'General Dentistry',
                to: `/general-dentistry${locationSuffix}`,
                children: [
                    { label: 'Dental Exams and Cleanings', to: `/dental-exams-and-cleanings${locationSuffix}` },
                    { label: 'Dental Cleaning and Prevention', to: `/dental-cleaning-and-prevention${locationSuffix}` },
                    {
                        label: 'Fillings',
                        to: `/fillings${locationSuffix}`,
                        children: [
                            { label: 'Glass Ionomer Fillings', to: `/glass-ionomer-fillings${locationSuffix}` },
                            { label: 'Composite Fillings', to: `/composite-fillings${locationSuffix}` },
                            { label: 'Amalgam Fillings (Silver Fillings)', to: `/amalgam-fillings${locationSuffix}` },
                        ],
                    },
                    {
                        label: 'Preventive Dentistry',
                        to: `/preventive-dentistry${locationSuffix}`,
                        children: [
                            { label: 'Gingivitis', to: `/gingivitis${locationSuffix}` },
                        ],
                    },
                ],
            },
            {
                label: 'Endodontics',
                to: `/endodontics${locationSuffix}`,
                children: [
                    { label: 'Root Canal Treatment', to: `/root-canal-treatment${locationSuffix}` },
                    { label: 'Pulpotomies', to: `/pulpotomies${locationSuffix}` },
                ],
            },
            {
                label: 'Prosthodontics',
                to: `/prosthodontics${locationSuffix}`,
                children: [
                    { label: 'Dentures/Partial Dentures', to: `/dentures-partial-dentures${locationSuffix}` },
                    { label: 'Bridges', to: `/dental-bridges${locationSuffix}` },
                    { label: 'Inlay & Onlay Restorations', to: `/inlay-onlay-restorations${locationSuffix}` },
                    { label: 'Dental Implants', to: `/dental-implants${locationSuffix}` },
                ],
            },
            {
                label: 'Pediatric Dentistry',
                to: `/pediatric-dentistry${locationSuffix}`,
                children: [
                    { label: 'Custom Sports Mouthguards', to: `/custom-sports-mouthguards${locationSuffix}` },
                    { label: 'Fluoride Trays', to: `/fluoride-trays${locationSuffix}` },
                ],
            },
            {
                label: 'Restorative Dentistry',
                to: `/restorative-dentistry${locationSuffix}`,
                children: [
                    { label: 'Oral Cancer Screening', to: `/oral-cancer-screening${locationSuffix}` },
                ],
            },
            // Flat service links (no children)
            { label: 'Cosmetic Dentistry', to: '/cosmetic-dentistry' },
            { label: 'Periodontists', to: `/periodontics${locationSuffix}` },
            { label: 'Orthodontics', to: `/orthodontics${locationSuffix}` },
            { label: 'Emergency Services', to: `/emergency-services${locationSuffix}` },
        ],
    },

    // 3. Blog
    { label: 'Blog', to: '/blog' },

    // 4. Book Appointment
    { label: 'Book Appointment', to: '/book-appointment' },

    // 5. Patient Portal
    { label: 'Patient Portal', to: '/patient-portal' },

    // 6. Insurance (dropdown)
    {
        label: 'Insurance',
        to: '/insurance',
        children: [
            { label: 'Delta Dental', to: '/delta-dental' },
            { label: 'Blue Cross Blue Shield', to: '/blue-cross-blue-shield' },
            { label: 'Connection Dental', to: '/ppo' },
        ],
    },

    // 7. Medicaid
    { label: 'Medicaid', to: '/medicaid' },

    // 8. Medicare
    { label: 'Medicare', to: '/medicare' },

    // 9. Financing (dropdown)
    {
        label: 'Financing',
        to: '/financing',
        children: [
            { label: 'Cherry', to: '/cherry' },
            { label: 'In House Payment Plan', to: '/in-house-payment-plan' },
            { label: 'Sunbit', to: '/sunbit' },
            { label: 'Kleer Membership', to: CLINIC_CONFIG.membership, external: true },
            { label: 'Care Credit', to: '/care-credit' },
        ],
    },

    // 10. Pay Your Bill
    { label: 'Pay Your Bill', to: '/pay-your-bill' },

    // 11. Contact Us
    { label: 'Contact Us', to: '/contact-us' },
];
